import { SingleClientEntity } from '@/lib/entities/client.entity';
import { resolveBrandFromCompany } from '@/lib/brand/resolve-brand-theme';
import { decodeVersioned, encodeVersioned, readVersioned, writeVersioned } from '@/lib/storage/versioned-storage';
import { getStoreClass } from '@poly-state/core';
import { createStoreSelector } from '@poly-state/react';
import { CompanyResponse, ResolvedBrand } from '@saas/api-client';
import cookie from 'js-cookie';
import { createContext, useContext } from 'react';
import { queryClient } from '@/utils/reactQueryClient';
import { UserEntity } from './types';

type WhiteLabelingState = NonNullable<CompanyResponse['whiteLabeling']>;

// The unbranded workspace default. Listed as a full record so a workspace that clears its
// branding resets every field rather than leaving stale values from the previous workspace.
export const emptyWhitelabeling: WhiteLabelingState = {
	id: '',
	companyId: '',
	cfHostNameId: null,
	chainName: null,
	customAppName: null,
	customDomain: null,
	customDomainStatus: null,
	domainCheckedAt: null,
	domainFailureReason: null,
	endorsementEnabled: false,
	affiliationDisplayEnabled: false,
	faviconUrl: null,
	logoUrl: null,
	smsContent: null,
	smsTemplates: null,
	themeColor: null,
};

export type PublicMeetingFeatureAccess = {
	hasDrawing: boolean;
};

/** Every UI locale the app can display. */
export const APP_LANGUAGES = ['en', 'no', 'fi', 'de', 'sv', 'da'] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

export const isAppLanguage = (value: unknown): value is AppLanguage =>
	typeof value === 'string' && (APP_LANGUAGES as readonly string[]).includes(value);

export type GlobalStoreType = {
	authenticated: boolean;
	user: UserEntity | null;
	token: string;
	companies: CompanyResponse[];
	currentCompany: CompanyResponse | null;
	clients: SingleClientEntity[];
	language: AppLanguage;
	whitelabeling: WhiteLabelingState;
	// The single resolved-brand slot every app-chrome surface (rail, topbar, switcher, favicon,
	// title) reads. `null` is the neutral pre-resolve state: nothing branded until it resolves.
	resolvedBrand: ResolvedBrand | null;
	// A document-favicon override for route trees that theme outside the resolved-brand flow
	// (the customer portal). When set it wins over the resolved-brand/platform favicon.
	faviconOverride: string | null;
	publicMeetingFeatureAccess: PublicMeetingFeatureAccess;
	showOverly: boolean;
};

export const globalStoreInitialState: GlobalStoreType = {
	authenticated: false,
	user: null,
	token: '',
	companies: [],
	currentCompany: null,
	clients: [],
	language: 'en',
	whitelabeling: emptyWhitelabeling,
	resolvedBrand: null,
	faviconOverride: null,
	publicMeetingFeatureAccess: {
		hasDrawing: false,
	},
	showOverly: false,
};

export type CookieStorage = {
	selectedCompanyID: string | null;
	token: string;
};

const isCookieStorage = (value: unknown): value is CookieStorage =>
	typeof value === 'object' &&
	value !== null &&
	'token' in value &&
	typeof value.token === 'string' &&
	'selectedCompanyID' in value &&
	(value.selectedCompanyID === null || typeof value.selectedCompanyID === 'string');

const STORE_COOKIE_NAME = 'DEFAULT_DATA';

const decodeCookieValue = (value: string): string | null => {
	try {
		return decodeURIComponent(value);
	} catch {
		return null;
	}
};

const readStoreCookieValues = (): string[] =>
	document.cookie
		.split('; ')
		.filter((entry) => entry.startsWith(`${STORE_COOKIE_NAME}=`))
		.map((entry) => entry.slice(STORE_COOKIE_NAME.length + 1));

export const getGlobalStoreCookie = (): CookieStorage | null => {
	for (const value of readStoreCookieValues()) {
		const data = decodeVersioned({ raw: decodeCookieValue(value), isValid: isCookieStorage });
		if (data) return data;
	}
	return null;
};

export type CurrentCompanyResolution = {
	companies: CompanyResponse[];
	/** Workspace subdomain from the address bar, `null` when the URL names none. */
	tenantSubdomain: string | null;
	/** Last selection persisted in the `DEFAULT_DATA` cookie. */
	selectedCompanyID: string | null;
};

/**
 * Picks the workspace a session boots into. The subdomain is the address the user asked for, so it
 * wins over the cookie's last selection; a subdomain nobody has access to falls back to the cookie
 * and leaves the reconciliation (clear + redirect) to the app-domain routing effect.
 */
export const resolveCurrentCompany = ({
	companies,
	tenantSubdomain,
	selectedCompanyID,
}: CurrentCompanyResolution): CompanyResponse | null => {
	const companyFromSubdomain = tenantSubdomain
		? companies.find((company) => company.subDomain === tenantSubdomain)
		: undefined;

	const companyFromCookie = selectedCompanyID
		? companies.find((company) => company.id === selectedCompanyID)
		: undefined;

	return companyFromSubdomain ?? companyFromCookie ?? null;
};

export const LANGUAGE_STORAGE_KEY = 'app:language';

export const readStoredLanguage = (): AppLanguage | null => {
	if (typeof window === 'undefined') return null;
	return readVersioned({ key: LANGUAGE_STORAGE_KEY, isValid: isAppLanguage });
};

const writeStoredLanguage = (lang: AppLanguage) => {
	if (typeof window === 'undefined') return;
	writeVersioned({ key: LANGUAGE_STORAGE_KEY, data: lang });
};

/** Browser language prefixes each app language answers to; Bokmål and Nynorsk both read Norwegian. */
const BROWSER_LANGUAGE_PREFIXES: Record<AppLanguage, readonly string[]> = {
	en: ['en'],
	no: ['no', 'nb', 'nn'],
	fi: ['fi'],
	de: ['de'],
	sv: ['sv'],
	da: ['da'],
};

/** The first language in the browser's preference list that the app speaks, if any. */
export const browserLanguage = (): AppLanguage | null => {
	if (typeof navigator === 'undefined') return null;
	const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
	for (const tag of preferred) {
		const prefix = tag?.toLowerCase().split('-')[0];
		const match = APP_LANGUAGES.find((language) => BROWSER_LANGUAGE_PREFIXES[language].includes(prefix ?? ''));
		if (match) return match;
	}
	return null;
};

/** A remembered choice, then the account's language, then the browser's, then English. */
export const resolveLanguage = (preferred?: string | null): AppLanguage => {
	const stored = readStoredLanguage();
	if (stored) return stored;
	if (isAppLanguage(preferred)) return preferred;
	return browserLanguage() ?? 'en';
};

export const isInIframe = (): boolean => {
	try {
		return window.self !== window.top;
	} catch {
		return true;
	}
};

export const setGlobalStoreCookie = (updater: (prev: CookieStorage | null) => CookieStorage | null) => {
	const data = updater(getGlobalStoreCookie());
	const currentDomain = window.location.hostname.includes(import.meta.env.VITE_APP_DOMAIN)
		? import.meta.env.VITE_APP_DOMAIN
		: window.location.hostname;

	// Use sameSite 'none' in iframe context so the cookie persists across iframe loads
	const sameSite = isInIframe() ? 'none' : 'strict';

	if (!data) {
		cookie.remove(STORE_COOKIE_NAME, {
			secure: true,
			expires: 365,
			domain: currentDomain,
			sameSite,
		});

		return;
	}

	cookie.set(STORE_COOKIE_NAME, encodeVersioned({ data }), {
		secure: true,
		expires: 365,
		domain: currentDomain,
		sameSite,
	});
};

export class GlobalStore extends getStoreClass<GlobalStoreType>() {
	constructor(init: GlobalStoreType = globalStoreInitialState) {
		super(init);
	}

	setLanguage = (value: AppLanguage | ((oldValue: AppLanguage) => AppLanguage)) => {
		const next = typeof value === 'function' ? value(this.getState().language) : value;
		writeStoredLanguage(next);
		this.setState((p) => ({ ...p, language: next }));
	};

	// Sets the active language for this session only, without persisting to localStorage. The customer
	// portal uses this so its Norwegian locale never overwrites (or outlives) the operator's own choice.
	setLanguageTransient = (lang: AppLanguage) => {
		this.setState((p) => ({ ...p, language: lang }));
	};

	onCompanySelection = (workspace: CompanyResponse) => {
		const previousId = this.getState().currentCompany?.id ?? null;
		if (previousId && previousId !== workspace.id) queryClient.clear();
		this.setState((p) => ({
			...p,
			currentCompany: workspace,
			// Keep the derived `whitelabeling` slice (read by display components + the theme
			// applier) in sync with the workspace we just switched to, so branding/colour can't
			// bleed over from the previously selected workspace.
			whitelabeling: workspace.whiteLabeling ?? globalStoreInitialState.whitelabeling,
			// Re-resolve the app-chrome Brand off the newly selected workspace so the rail,
			// topbar, switcher, favicon, and title re-theme in place without a reload.
			resolvedBrand: resolveBrandFromCompany(workspace),
		}));

		const state = this.getState();

		setGlobalStoreCookie(() => {
			return {
				selectedCompanyID: workspace.id,
				token: state.token,
			};
		});
	};

	/**
	 * Update the active workspace's white-labeling. `whitelabeling` (read by display components and
	 * the theme applier) and `currentCompany.whiteLabeling` are denormalized copies of the same data;
	 * writing them together in one action is what keeps them from drifting — so every branding edit
	 * goes through here rather than touching one slice in isolation.
	 */
	setCompanyWhitelabeling = (whiteLabeling: CompanyResponse['whiteLabeling']) => {
		this.setState((p) => {
			const base = p.currentCompany;
			const currentCompany = base ? { ...base, whiteLabeling: whiteLabeling ?? null } : base;
			// A chain member's chrome wears the chain parent (server-resolved chromeBrand, whose companyId
			// differs from the member's), and the member can only edit domain/SMS, so its chrome never
			// changes here. A self-branded workspace re-derives its chrome from the just-edited whiteLabeling.
			const memberChrome = base?.chromeBrand && base.chromeBrand.companyId !== base.id ? base.chromeBrand : null;
			return {
				...p,
				whitelabeling: whiteLabeling ?? globalStoreInitialState.whitelabeling,
				currentCompany,
				resolvedBrand:
					memberChrome ?? resolveBrandFromCompany(currentCompany ? { ...currentCompany, chromeBrand: null } : null),
			};
		});
	};

	onLogin = (res: { user: UserEntity; token: string }) => {
		this.setState((p) => ({
			...p,
			authenticated: true,
			token: res.token,
			user: res.user,
			// Prefer the language picked in the switcher, then the user's stored language, then
			// browser locale, then 'en' (VAD-233). `resolveLanguage` holds that order, and the
			// state's own language is not consulted: it is already a resolved default, so reading
			// it here would always win and the account's own language would never be applied.
			language: resolveLanguage(res.user.language),
		}));

		setGlobalStoreCookie(() => {
			return {
				selectedCompanyID: null,
				token: res.token,
			};
		});
	};

	/**
	 * Swaps the bearer token in place after the server minted a fresh session for the User who is
	 * already signed in — setting a first password revokes every other session, so the token this
	 * tab holds stops working the moment the call returns. Unlike `onLogin` it keeps the workspace
	 * selection, because nothing about which company is open has changed.
	 */
	setSessionToken = (token: string) => {
		this.setState((p) => ({ ...p, token }));
		setGlobalStoreCookie((prev) => ({ selectedCompanyID: prev?.selectedCompanyID ?? null, token }));
	};

	onLogout = () => {
		// Reload only when a session existed; reloading while already logged out loops forever.
		const hadSession = !!this.getState().token;
		setGlobalStoreCookie(() => null);
		localStorage.clear();
		this.setState({ ...globalStoreInitialState, language: this.getState().language });
		if (hadSession) window?.location?.reload();
	};
}

export const GlobalStoreContext = createContext<GlobalStore | null>(null);

export const useGlobalStore = () => {
	const store = useContext(GlobalStoreContext);

	if (!store) {
		throw new Error('useGlobalStore must be used within a GlobalStoreProvider.');
	}

	const select = createStoreSelector(store);

	return { store, select };
};

export const useGlobalStoreSelector = <U>(fn: (store: GlobalStoreType) => U): U => {
	const { select } = useGlobalStore();

	return select(fn);
};
